// DocumentTemplatesPage.jsx
//
// CRUD for document_templates + render-against-matter preview.
//
// Templates are firm-scoped boilerplate (engagement letters, demand
// letters, court filings, etc.) with {{path.to.value}} placeholders.
// Lawyers create them once, then "Use template" against a matter to
// produce a finished document with all the merge-field substitutions
// applied. Variable substitution is done client-side via
// `renderTemplate()` from src/lib/templateRender.js — no server round-trip,
// instant preview.
//
// What this page does (Phase 1 / v1.0.37):
//   * List all firm templates (search by name + category filter)
//   * Create a new template (name, description, category, body)
//   * Edit / delete a template
//   * "Use template" modal — pick a matter, see the rendered output,
//     copy to clipboard / download as .txt
//
// Phase 2 (future):
//   * Save rendered output as a firm document (integrates with the
//     existing documents table)
//   * Template-library starter pack (common Ghana legal templates)
//   * Server-side render to .docx / .pdf (currently text-only)
//   * Conditional sections / loops (if we need them — most templates
//     don't)

import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, Plus, Search, Trash2, Edit2, Copy, Download, Play, Tag, X,
} from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import {
  listDocumentTemplates,
  createDocumentTemplate,
  updateDocumentTemplate,
  deleteDocumentTemplate,
  listMatters,
  getMatter,
  fetchContact,
} from '../lib/db';
import {
  Card, CardHeader, CardBody, Button, Input, Textarea, Label,
  Modal, EmptyState, ConfirmDeleteModal, Badge, SkeletonListRows,
} from '../components/ui';
import { renderTemplate, extractPlaceholders, buildContext } from '../lib/templateRender';

const BLANK_TEMPLATE = {
  id:          null,
  name:        '',
  description: '',
  category:    '',
  body:        '',
};

// The merge fields the UI advertises to template authors. Driven from
// our standard buildContext() shape so the helper card on the editor
// matches what's actually available at render time.
const AVAILABLE_FIELDS = [
  { group: 'Firm',   field: '{{firm.name}}',            desc: "The firm's display name" },
  { group: 'Firm',   field: '{{firm.email}}',           desc: 'Firm contact email' },
  { group: 'Firm',   field: '{{firm.phone}}',           desc: 'Firm contact phone' },
  { group: 'Firm',   field: '{{firm.address}}',         desc: 'Firm postal address' },
  { group: 'Matter', field: '{{matter.title}}',         desc: 'Matter / case title' },
  { group: 'Matter', field: '{{matter.case_number}}',   desc: 'Court / file number' },
  { group: 'Matter', field: '{{matter.status}}',        desc: 'Current matter status' },
  { group: 'Matter', field: '{{matter.opened_at}}',     desc: 'Date the matter was opened' },
  { group: 'Client', field: '{{client.full_name}}',     desc: "Client's full name" },
  { group: 'Client', field: '{{client.email}}',         desc: "Client's email address" },
  { group: 'Client', field: '{{client.phone}}',         desc: "Client's phone number" },
  { group: 'Client', field: '{{client.address}}',       desc: "Client's postal address" },
  { group: 'User',   field: '{{user.full_name}}',       desc: 'Currently signed-in lawyer' },
  { group: 'User',   field: '{{user.email}}',           desc: 'Signed-in lawyer email' },
  { group: 'Date',   field: '{{today}}',                desc: "Today's date — YYYY-MM-DD" },
  { group: 'Date',   field: '{{now}}',                  desc: 'Current timestamp' },
];

export default function DocumentTemplatesPage() {
  const { firm, firmId }   = useFirm();
  const { userProfile }    = useAuth();

  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [editing, setEditing]       = useState(null);    // template object or BLANK_TEMPLATE
  const [deleting, setDeleting]     = useState(null);    // template to confirm deletion
  const [using, setUsing]           = useState(null);    // template to render

  useEffect(() => {
    if (!firmId) return;
    let cancelled = false;
    setLoading(true);
    listDocumentTemplates(firmId).then(({ data, error }) => {
      if (cancelled) return;
      if (error) console.error('[Templates] list failed:', error);
      setTemplates(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [firmId]);

  // Unique category list for the filter chips.
  const categories = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => { if (t.category) set.add(t.category); });
    return Array.from(set).sort();
  }, [templates]);

  // Filtered view.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (categoryFilter && t.category !== categoryFilter) return false;
      if (!needle) return true;
      return (t.name || '').toLowerCase().includes(needle)
          || (t.description || '').toLowerCase().includes(needle);
    });
  }, [templates, search, categoryFilter]);

  // ── Mutations ──────────────────────────────────────────────────
  const refresh = async () => {
    const { data } = await listDocumentTemplates(firmId);
    setTemplates(data || []);
  };

  const handleSave = async (form) => {
    const mergeFields = extractPlaceholders(form.body || '');
    if (form.id) {
      await updateDocumentTemplate(form.id, { ...form, mergeFields });
    } else {
      await createDocumentTemplate({ firmId, ...form, mergeFields });
    }
    await refresh();
    setEditing(null);
  };

  const handleDelete = async () => {
    if (!deleting?.id) return;
    await deleteDocumentTemplate(deleting.id);
    await refresh();
    setDeleting(null);
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Document templates</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Boilerplate documents with merge fields. Create once, generate per-matter.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setEditing({ ...BLANK_TEMPLATE })}>
          New template
        </Button>
      </div>

      {/* Search + category chips */}
      {!loading && templates.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or description…"
              className="pl-9"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <CategoryChip
                label="All"
                active={categoryFilter === ''}
                onClick={() => setCategoryFilter('')}
              />
              {categories.map((c) => (
                <CategoryChip
                  key={c}
                  label={c}
                  active={categoryFilter === c}
                  onClick={() => setCategoryFilter(c)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <Card><CardBody><SkeletonListRows rows={4} /></CardBody></Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={FileText}
              title={templates.length === 0 ? 'No templates yet' : 'No templates match your search'}
              description={
                templates.length === 0
                  ? 'Create your first template — engagement letters, demand notices, court filings.'
                  : 'Try a different search term or clear the filter.'
              }
              action={
                templates.length === 0
                  ? <Button icon={Plus} onClick={() => setEditing({ ...BLANK_TEMPLATE })}>Create your first template</Button>
                  : null
              }
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              onUse={() => setUsing(t)}
              onEdit={() => setEditing(t)}
              onDelete={() => setDeleting(t)}
            />
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {editing && (
        <TemplateEditor
          template={editing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {/* Confirm delete */}
      {deleting && (
        <ConfirmDeleteModal
          open={!!deleting}
          onClose={() => setDeleting(null)}
          onConfirm={handleDelete}
          title="Delete template?"
          itemLabel={deleting.name}
          warning="Generated documents from this template are not affected. The template itself will be permanently removed."
        />
      )}

      {/* Use template — render against a matter */}
      {using && (
        <TemplateUseModal
          template={using}
          firm={firm}
          userProfile={userProfile}
          firmId={firmId}
          onClose={() => setUsing(null)}
        />
      )}
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────

function CategoryChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition ${
        active
          ? 'bg-indigo-600 text-white'
          : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
      }`}
    >
      {label}
    </button>
  );
}

function TemplateRow({ template, onUse, onEdit, onDelete }) {
  const placeholders = useMemo(
    () => extractPlaceholders(template.body || ''),
    [template.body],
  );
  return (
    <Card>
      <CardBody>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {template.name}
              </h3>
              {template.category && (
                <Badge tone="indigo"><Tag size={10} className="inline mr-1" />{template.category}</Badge>
              )}
              {!template.is_active && <Badge tone="slate">Inactive</Badge>}
            </div>
            {template.description && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{template.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{(template.body || '').length} chars</span>
              <span>·</span>
              <span>{placeholders.length} merge field{placeholders.length === 1 ? '' : 's'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="primary" icon={Play} onClick={onUse}>Use</Button>
            <Button variant="ghost"   icon={Edit2} onClick={onEdit}>Edit</Button>
            <Button variant="ghost"   icon={Trash2} onClick={onDelete} className="text-red-600 hover:text-red-700" />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function TemplateEditor({ template, onCancel, onSave }) {
  const [form, setForm] = useState({
    id:          template.id || null,
    name:        template.name || '',
    description: template.description || '',
    category:    template.category || '',
    body:        template.body || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const placeholders = useMemo(() => extractPlaceholders(form.body || ''), [form.body]);
  const nameValid    = form.name.trim().length > 0;
  const bodyValid    = form.body.length > 0;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!nameValid || !bodyValid) {
      setError('Name and body are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || 'Could not save the template.');
      setSaving(false);
    }
  };

  // Insert a merge field at the cursor (or append).
  const insertField = (field) => {
    setForm((f) => ({ ...f, body: (f.body || '') + field }));
  };

  return (
    <Modal
      open
      onClose={() => !saving && onCancel()}
      title={form.id ? 'Edit template' : 'New template'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!nameValid || !bodyValid || saving} loading={saving}>
            {form.id ? 'Save changes' : 'Create template'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label required>Template name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Engagement letter — Civil"
              autoFocus
            />
          </div>
          <div>
            <Label>Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Litigation"
            />
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What this template is for — visible in the list"
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Label required>Template body</Label>
            <Textarea
              rows={14}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder={`Dear {{client.full_name}},\n\nRe: {{matter.title}}...`}
              className="font-mono text-xs leading-relaxed"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
              Use <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[11px]">{'{{path.to.value}}'}</code> placeholders. Click any field on the right to insert.
            </p>
          </div>

          <div>
            <Label>Available merge fields</Label>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 max-h-[360px] overflow-y-auto">
              {Object.entries(groupBy(AVAILABLE_FIELDS, 'group')).map(([group, fields]) => (
                <div key={group}>
                  <div className="sticky top-0 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700">
                    {group}
                  </div>
                  {fields.map((f) => (
                    <button
                      type="button"
                      key={f.field}
                      onClick={() => insertField(f.field)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-slate-100 dark:border-slate-800 last:border-b-0 transition"
                    >
                      <code className="text-[11px] font-mono text-indigo-700 dark:text-indigo-300 block">{f.field}</code>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">{f.desc}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {placeholders.length > 0 && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
                This template uses {placeholders.length} field{placeholders.length === 1 ? '' : 's'}: {placeholders.slice(0, 5).map((p) => <code key={p} className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] mr-1">{p}</code>)}
                {placeholders.length > 5 && <span>and {placeholders.length - 5} more</span>}
              </p>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

function TemplateUseModal({ template, firm, userProfile, firmId, onClose }) {
  const [matters, setMatters]   = useState([]);
  const [matterId, setMatterId] = useState('');
  const [rendered, setRendered] = useState('');
  const [loading, setLoading]   = useState(true);
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    if (!firmId) return;
    let cancelled = false;
    listMatters(firmId).then(({ data }) => {
      if (cancelled) return;
      setMatters(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [firmId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let matter = null;
      let client = null;
      if (matterId) {
        const { data: m } = await getMatter(matterId);
        matter = m || null;
        if (matter?.client_id) {
          const { data: c } = await fetchContact(matter.client_id);
          client = c || null;
        }
      }
      if (cancelled) return;
      const ctx = buildContext({
        firm,
        matter,
        client,
        user: userProfile,
      });
      setRendered(renderTemplate(template.body || '', ctx));
    })();
    return () => { cancelled = true; };
  }, [matterId, template, firm, userProfile]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers — fall back to nothing; user can select+copy manually.
    }
  };

  const handleDownload = () => {
    const blob = new Blob([rendered], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${template.name.replace(/[^a-z0-9-_ ]/gi, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Use template: ${template.name}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="secondary" icon={Copy} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to clipboard'}
          </Button>
          <Button variant="primary" icon={Download} onClick={handleDownload}>
            Download .txt
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Render for matter</Label>
          <select
            value={matterId}
            onChange={(e) => setMatterId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            disabled={loading}
          >
            <option value="">(no matter — firm + user fields only)</option>
            {matters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}{m.case_number ? ` — ${m.case_number}` : ''}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
            Picking a matter pulls in {`{{matter.*}}`} and {`{{client.*}}`} fields. Without one, only firm + user + date fields render.
          </p>
        </div>

        <div>
          <Label>Rendered output</Label>
          <pre className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 text-xs leading-relaxed whitespace-pre-wrap font-mono max-h-[420px] overflow-y-auto">
            {rendered || <span className="text-slate-400">Pick a matter above to see the rendered template.</span>}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

// Helper — tiny groupBy because we don't want a lodash dep for one place.
function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = item[key];
    (acc[k] ||= []).push(item);
    return acc;
  }, {});
}
